import { memo } from 'react';
import Table from '@/components/general/Table/Table';
import { useTransactionsQuery } from '@/hooks/queries/wallet/walletQueries';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import Status from '@/components/blocks/wallet/Transactions/general/Status/Status';
import { TransactionDate } from '@/components/blocks/wallet/Transactions/general/TransactionDate/TransactionDate';
import { WALLET_PHRASES } from '@/consts/phrases/walletPhrases';
import { Currency } from '@/components/general/Currency/Currency';
import { NonTaxable } from '@/components/blocks/wallet/NonTaxable/NonTaxable';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { SkeletonBlock } from '@/components/general/skeletonBlock/SkeletonBlock';
import { WalletPaymentIcon } from '@/components/blocks/wallet/Payment/WalletPaymentIcon/WalletPaymentIcon';
import { PaymentAmount } from '../../../PaymentAmount/PaymentAmount';
import { InvoiceFileButton } from '../blocks/InvoiceFileButton/InvoiceFileButton';
import { isFeatureEnabled } from '@/config';
import { IAccount, ITransaction } from '@/types/types/forms/wallet/wallet.interface';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface TransactionTableProps {
    selectedAccount: IAccount;
    showArchivedTransactions: boolean;
    participationKey: string | undefined;
    setShowArchivedTransactions: (showArchivedTransactions: boolean) => void;
    handleStatusClicked: (transactionId: number) => void;
    handleDisplayAmazonGiftCardClicked: (
        transactionId: number,
        transactionMethodId: number,
    ) => void;
    handlePrepaidCardHelpClicked: (transactionId: number) => void;
    handleCancelTransactionClicked: (transactionId: number) => void;
}

const TransactionTable = (props: TransactionTableProps) => {
    const { t } = usePhrase();
    const areNewWalletMethodsEnabled = isFeatureEnabled('newWalletMethods');

    const { transactionsWithCount, isLoading } = useTransactionsQuery(
        props.selectedAccount.accountId,
        props.showArchivedTransactions,
    );

    const shouldShowArchivedTransactionsButton =
        transactionsWithCount && transactionsWithCount?.count < transactionsWithCount?.total;

    const handleShowArchivedTransactionsButtonClicked = () => {
        props.setShowArchivedTransactions(true);
    };

    const getAmountLabel = (trans: ITransaction, selectedAccount: IAccount) => {
        const hasAmount = trans.showAmount && trans.amount; // Please note that currency code and currency position can be undefined
        if (hasAmount) {
            if (areNewWalletMethodsEnabled) {
                return (
                    <PaymentAmount
                        transactionData={{
                            currencyCode: selectedAccount.currencyCode,
                            currencySymbol: selectedAccount.currencySymbol,
                            amount: trans.amount,
                            currencySymbolPosition: selectedAccount.currencySymbolPosition,
                            balanceNonTaxable: selectedAccount.balanceNonTaxable,
                        }}
                        amount={trans.amount}
                        vatAmount={trans.vatAmount}
                        listingView
                    />
                );
            } else {
                return (
                    <Currency
                        code={selectedAccount.currencyCode}
                        amount={trans.amount}
                        symbol={selectedAccount.currencySymbol}
                        position={selectedAccount.currencySymbolPosition}
                    />
                );
            }
        } else {
            return t(WALLET_PHRASES.TBC);
        }
    };

    const getDescriptionLabel = (trans: ITransaction) => {
        return (
            <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    width: ['initial', 'initial', trans.showAmazonDisplayCode ? 340 : 'initial'],
                }}
            >
                <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    {!!trans.paymentMethodId && <WalletPaymentIcon id={trans.paymentMethodId} />}
                    <Box>
                        {trans.descriptionPhraseId
                            ? t(trans.descriptionPhraseId, {
                                  replaceTextVars: [trans.projectNumber],
                              })
                            : trans.description}
                    </Box>
                </Stack>
                {trans.showPrepaidCardsHelp && (
                    <Button
                        onClick={() => props.handlePrepaidCardHelpClicked(trans.transactionId)}
                        size="small"
                        variant="outlined"
                        color="info"
                        sx={{ fontSize: '0.875rem' }}
                    >
                        <HelpOutlineIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                        {t(WALLET_PHRASES.GET_HELP)}
                    </Button>
                )}
                {trans.showAmazonDisplayCode && (
                    <Button
                        onClick={() =>
                            props.handleDisplayAmazonGiftCardClicked(
                                trans.transactionId,
                                trans.paymentMethodId,
                            )
                        }
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.875rem' }}
                    >
                        {t(WALLET_PHRASES.DISPLAY_CODE)}
                    </Button>
                )}
                {areNewWalletMethodsEnabled && !!trans.invoiceFile && (
                    <InvoiceFileButton invoiceFileApiUrl={trans.invoiceFile} />
                )}
            </Stack>
        );
    };

    const getBalanceLabel = (trans: ITransaction, selectedAccount: IAccount) => {
        if (trans.showBalance && !trans.showCancelPaymentButton) {
            return (
                <Typography
                    variant="subtitle1"
                    sx={(theme) => ({ fontWeight: '400', color: theme.palette.text.primary })}
                >
                    <Currency
                        code={selectedAccount.currencyCode}
                        amount={trans.balance}
                        symbol={selectedAccount.currencySymbol}
                        position={selectedAccount.currencySymbolPosition}
                    />
                </Typography>
            );
        } else if (trans.showCancelPaymentButton) {
            return (
                <Button
                    onClick={() => props.handleCancelTransactionClicked(trans.transactionId)}
                    size="small"
                    variant="contained"
                    sx={{ fontSize: '0.875rem' }}
                >
                    {t(WALLET_PHRASES.CANCEL)}
                </Button>
            );
        }
        return null;
    };

    return (
        <>
            {isLoading && <SkeletonBlock amount={4} height={75} />}
            {!isLoading && (
                <Stack>
                    <Table
                        data-testid="table"
                        paginationLimit={transactionsWithCount?.count}
                        paginationVisible={false}
                        tableData={{
                            details: {
                                'aria-label': 'transactions-table',
                            },
                            headers: [
                                { label: t(WALLET_PHRASES.DATE), key: 'date' },
                                { label: t(WALLET_PHRASES.REFERENCE_NUMBER), key: 'referenceNum' },
                                { label: t(WALLET_PHRASES.TYPE), key: 'type' },
                                {
                                    label: t(WALLET_PHRASES.DESCRIPTION),
                                    key: 'description',
                                    fullwidth: true,
                                },
                                { label: t(WALLET_PHRASES.AMOUNT), key: 'amount' },
                                { label: t(WALLET_PHRASES.STATUS), key: 'status' },
                                { label: t(WALLET_PHRASES.BALANCE), key: 'balance' },
                            ],
                            rows: (transactionsWithCount?.transactions || []).map((trans) => ({
                                key: `${trans.transactionId}`,
                                isHighlighted:
                                    trans.participationKey === Number(props.participationKey),
                                values: [
                                    {
                                        label: (
                                            <Box>
                                                {trans.completeDt ? (
                                                    <TransactionDate date={trans.completeDt} />
                                                ) : (
                                                    ''
                                                )}
                                            </Box>
                                        ),
                                        key: 'date',
                                    },
                                    {
                                        label: <Box>{trans.transactionId}</Box>,
                                        key: 'referenceNum',
                                    },
                                    {
                                        label: (
                                            <Box>
                                                {t(trans.typePhraseId)}{' '}
                                                {trans.nonTaxable && <NonTaxable />}
                                            </Box>
                                        ),
                                        key: 'type',
                                        fullwidth: true,
                                    },
                                    {
                                        label: getDescriptionLabel(trans),
                                        key: 'description',
                                        fullwidth: true,
                                    },
                                    {
                                        label: (
                                            <Box>
                                                {getAmountLabel(trans, props.selectedAccount)}
                                            </Box>
                                        ),
                                        key: 'amount',
                                    },
                                    {
                                        label: (
                                            <Box
                                                sx={{
                                                    cursor: 'pointer',
                                                    display: 'inline-flex',
                                                    justifyContent: [
                                                        'flex-end',
                                                        'flex-end',
                                                        'flex-start',
                                                    ],
                                                }}
                                                onClick={() =>
                                                    props.handleStatusClicked(trans.transactionId)
                                                }
                                            >
                                                <Status
                                                    statusColor={trans.statusColor}
                                                    statusPhraseId={trans.statusPhraseId}
                                                    title={WALLET_PHRASES.TRANSACTION_HISTORY}
                                                />
                                            </Box>
                                        ),
                                        key: 'status',
                                    },
                                    {
                                        label: (
                                            <Box>
                                                {getBalanceLabel(trans, props.selectedAccount)}
                                            </Box>
                                        ),
                                        key: 'balance',
                                    },
                                ],
                            })),
                        }}
                    />
                    {shouldShowArchivedTransactionsButton && (
                        <Grid
                            container
                            sx={{
                                justifyContent: 'flex-end',
                                mt: 2,
                            }}
                        >
                            <Button
                                onClick={handleShowArchivedTransactionsButtonClicked}
                                sx={{ px: 2 }}
                                variant="contained"
                                size="small"
                            >
                                {t(WALLET_PHRASES.SHOW_ARCHIVED_TRANSACTIONS)}
                            </Button>
                        </Grid>
                    )}
                </Stack>
            )}
        </>
    );
};

export default memo(TransactionTable);
